import { useMemo, useState } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import {
  MdRemove as DeleteIcon,
  MdLink as ViewIcon,
  MdSettingsBackupRestore as ResetPriceIcon,
  MdHourglassEmpty as LoadingIcon
} from 'react-icons/md';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import axiosAPI from '../../../../../utils/client/axiosAPI';
import { currencyFormat } from '../../../../../utils/helpers/helpers';
import cx from 'classnames';
import style from '../create-order.module.scss';
import { useCartContext } from '../../../../../context/CartContext/CartContext';
import { Product } from '../../../../../models/Product/Product';

interface CartProduct {
  id: number;
  quantity: number;
}

interface DataType {
  key: number; // product.id
  sku: string;
  name: string;
  category: string;
  price: number;
  quantity: number;
  productId: number;
}

const OrderTable: React.FC = () => {
  const { cart, cartRemoveProduct } = useCartContext();
  const cartProducts: CartProduct[] = useMemo(
    () => cart?.products.map((p) => ({ id: p.id, quantity: p.quantity })) ?? [],
    [cart?.products]
  );
  const queryClient = useQueryClient();

  // Fetch full product details once
  const { data: allProducts } = useQuery(
    ['all-products-for-order'],
    () => Product.listForOrder(),
    { staleTime: 5 * 60 * 1000 }
  );

  // Build table rows by enriching cart items
  const data: DataType[] = useMemo(() => {
    if (!allProducts) return [];
    return cartProducts
      .map(({ id, quantity }) => {
        const prod = allProducts.find((p) => p.id === id);
        if (!prod) return null;
        return {
          key: id,
          sku: prod.sku ?? 'N/A',
          name: prod.name ?? 'N/A',
          category: prod.category?.name ?? 'N/A',
          price: prod.price ?? 0,
          quantity,
          productId: id
        };
      })
      .filter((row): row is DataType => row !== null);
  }, [allProducts, cartProducts]);

  const [resettingId, setResettingId] = useState<number | null>(null);
  const { mutate: resetPrice, isLoading: isResetting } = useMutation<
    any,
    Error,
    number
  >(
    async (productId) => {
      setResettingId(productId);
      const res = await axiosAPI.post(
        `/admin/products/${productId}/reset-price-to-category`
      );
      return res.data.data ?? res.data;
    },
    {
      onSuccess: (updated) => {
        toast.success(
          updated?.name
            ? `Cena za proizvod "${updated.name}" je resetovana!`
            : 'Cena proizvoda je resetovana!'
        );
        queryClient.invalidateQueries(['cart']);
      },
      onError: (err: any) => {
        toast.error(
          err.response?.data?.message || 'Greška pri resetovanju cene.'
        );
      },
      onSettled: () => setResettingId(null)
    }
  );

  const columns: ColumnsType<DataType> = [
    { title: 'SKU', dataIndex: 'sku', key: 'sku' },
    { title: 'Naziv', dataIndex: 'name', key: 'name' },
    { title: 'Kategorija', dataIndex: 'category', key: 'category' },
    {
      title: 'Cena (RSD)',
      dataIndex: 'price',
      key: 'price',
      align: 'right',
      render: (p) => currencyFormat(p)
    },
    {
      title: 'Količina',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center'
    },
    {
      title: 'Akcije',
      key: 'actions',
      align: 'center',
      render: (_, row) => {
        const resetting = isResetting && resettingId === row.productId;
        return (
          <div className={style.tableIcons}>
            <button
              className="cs-table-button-icon"
              onClick={() => cartRemoveProduct(row.productId)}
              disabled={resetting}
              title="Ukloni iz narudžbine"
            >
              <DeleteIcon />
            </button>
            <button
              className="cs-table-button-icon"
              onClick={() => !resetting && resetPrice(row.productId)}
              disabled={resetting}
              title="Resetuj cenu na cenu kategorije"
            >
              {resetting ? (
                <LoadingIcon className="animate-spin" />
              ) : (
                <ResetPriceIcon />
              )}
            </button>
            <a
              className="cs-table-button-icon"
              href={`/admin/product/${row.productId}`}
              target="_blank"
              rel="noopener noreferrer"
              title="Pogledaj proizvod"
              style={resetting ? { opacity: 0.5, pointerEvents: 'none' } : {}}
            >
              <ViewIcon />
            </a>
          </div>
        );
      }
    }
  ];

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey="key"
      pagination={false}
      className={cx(style.tableWrapper, 'cs-table-wrapper')}
      scroll={{ x: 'max-content' }}
    />
  );
};

export default OrderTable;
